import { useEffect, useMemo, useState } from 'react';
import api, { API_BASE } from '@/lib/api';
import { PageHeader, Stat, LoadingShell } from '@/components/ui-bits';
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Download, FileSpreadsheet, Play, Plus, Save, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#64748b', '#ec4899'];

async function downloadCsv(path, filename, options = {}) {
  const tok = localStorage.getItem('zunoid_access');
  const res = await fetch(`${API_BASE}${path}`, {
    method: options.method || 'GET',
    headers: {
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  if (!res.ok) {
    toast.error('No se pudo exportar');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('Descarga iniciada');
}

function OverviewTab({ days, setDays }) {
  const [data, setData] = useState(null);
  const [csatFrom, setCsatFrom] = useState('');
  const [csatTo, setCsatTo] = useState('');

  const load = async () => {
    setData(null);
    try {
      const { data: d } = await api.get(`/reports/summary?days=${days}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar reportes');
    }
  };

  useEffect(() => { load(); }, [days]);

  const exportCsat = () => {
    if (csatFrom || csatTo) {
      const q = new URLSearchParams();
      if (csatFrom) q.set('from_date', csatFrom);
      if (csatTo) q.set('to_date', csatTo);
      downloadCsv(`/reports/export/csat?${q}`, `csat_${csatFrom || 'start'}_${csatTo || 'end'}.csv`);
    } else {
      downloadCsv(`/reports/export/csat?days=${days}`, `csat_${days}d.csv`);
    }
  };

  if (!data) return <LoadingShell />;
  const k = data.kpis;

  return (
    <>
      <div className="flex justify-end mb-3">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input w-auto">
          <option value={7}>7 días</option>
          <option value={30}>30 días</option>
          <option value={90}>90 días</option>
          <option value={365}>1 año</option>
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Leads nuevos" value={k.leads_new} hint={`${k.leads_total} totales`} />
        <Stat label="Conversión" value={`${k.conversion_rate}%`} hint={`Win ${k.win_rate || 0}% · ${k.leads_won || 0} ganados`} accent={`${Math.min(100, k.conversion_rate)}%`} />
        <Stat label="Pipeline $" value={`$${(k.pipeline_value || 0).toLocaleString('es-MX')}`} hint={`${k.leads_open} abiertos`} />
        <Stat label="Campañas" value={k.campaigns} hint={`${k.campaign_messages_sent} envíos · ${k.campaign_reply_rate || 0}% resp.`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Stat label="Mensajes" value={(k.messages_out || 0) + (k.messages_in || 0)} hint={`WA ${k.messages_whatsapp || 0} · Email ${k.messages_email || 0} · Chat ${k.messages_webchat || 0}`} />
        <Stat label="Ganados / perdidos" value={`${k.leads_won || 0} / ${k.leads_lost || 0}`} />
        <Stat label="Tareas" value={k.tasks_open} hint={`${k.tasks_overdue} vencidas`} />
        <Stat label="Resp. campañas" value={k.campaign_replied || 0} hint={`${k.campaign_messages_failed || 0} fallos`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Stat
          label="1ª respuesta (prom.)"
          value={k.chat_avg_first_reply_min != null ? `${k.chat_avg_first_reply_min} min` : '—'}
          hint={k.chat_replies_sampled ? `${k.chat_replies_sampled} muestras` : 'Sin pares in→out'}
        />
        <Stat
          label="1ª respuesta (mediana)"
          value={k.chat_median_first_reply_min != null ? `${k.chat_median_first_reply_min} min` : '—'}
        />
        <Stat
          label="Cumplimiento SLA"
          value={k.chat_sla_hit_pct != null ? `${k.chat_sla_hit_pct}%` : '—'}
          hint={k.chat_sla_target_min != null ? `Objetivo ≤ ${k.chat_sla_target_min} min` : 'Configura en Ajustes'}
        />
        <Stat label="Esperando ahora" value={k.chat_waiting_now ?? 0} hint="Último msg del cliente" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Stat
          label="CSAT promedio"
          value={k.csat_avg != null ? `${k.csat_avg} / 5` : '—'}
          hint={k.csat_count ? `${k.csat_count} calificaciones` : 'Sin CSAT en el periodo'}
        />
        <Stat
          label="CSAT (NPS-like)"
          value={k.csat_nps_like != null ? `${k.csat_nps_like}` : '—'}
          hint="5★ promotores − ≤2 detractores"
        />
        <Stat label="Webchat msgs" value={k.messages_webchat || 0} />
        <Stat label="Empresas" value={k.companies_total || 0} />
      </div>

      {(data.csat?.by_day?.length > 0 || data.csat?.by_channel?.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div className="surface p-5">
            <h2 className="font-display font-semibold mb-3">CSAT por día</h2>
            {(data.csat?.by_day || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin datos</p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.csat.by_day} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 5]} stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 10 }} />
                    <Bar dataKey="avg" name="Promedio" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="surface p-5">
            <h2 className="font-display font-semibold mb-3">CSAT por canal</h2>
            {(data.csat?.by_channel || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {data.csat.by_channel.map((row) => (
                  <div key={row.channel} className="flex items-center justify-between text-sm border-b border-border/40 py-2">
                    <span className="capitalize font-medium">{row.channel}</span>
                    <span className="tabular-nums">
                      <span className="text-amber-600 font-semibold">{row.avg}</span>
                      <span className="text-xs text-muted-foreground ml-2">({row.count})</span>
                    </span>
                  </div>
                ))}
                {data.csat?.distribution && (
                  <div className="pt-2 text-xs text-muted-foreground flex flex-wrap gap-2">
                    {[5, 4, 3, 2, 1].map((n) => (
                      <span key={n}>{n}★: {data.csat.distribution[String(n)] || 0}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {data.deals?.kpis && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <Stat
            label="Pipeline deals (pond.)"
            value={`$${(data.deals.kpis.weighted_pipeline || 0).toLocaleString('es-MX')}`}
            hint={`${(data.deals.kpis.deals_open || 0) + (data.deals.kpis.deals_negotiation || 0)} abiertos`}
          />
          <Stat label="Deals ganados $" value={`$${(data.deals.kpis.amount_won || 0).toLocaleString('es-MX')}`} hint={`${data.deals.kpis.deals_won || 0} deals`} />
          <Stat label="Win rate deals" value={`${data.deals.kpis.win_rate || 0}%`} hint={`${data.deals.kpis.deals_lost || 0} perdidos`} />
          <Stat label="Empresas" value={k.companies_total || 0} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 surface p-5">
          <h2 className="font-display font-semibold mb-4">Embudo</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 10 }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="surface p-5">
          <h2 className="font-display font-semibold mb-3 flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Exportar CSV
          </h2>
          <div className="space-y-2">
            <button type="button" className="btn-ghost w-full justify-start" onClick={() => downloadCsv(`/reports/export/leads?days=${days}`, `leads_${days}d.csv`)}>
              <Download className="h-4 w-4" /> Leads del periodo
            </button>
            <button type="button" className="btn-ghost w-full justify-start" onClick={() => downloadCsv(`/reports/export/agents?days=${days}`, `agentes_${days}d.csv`)}>
              <Download className="h-4 w-4" /> Rendimiento agentes
            </button>
            <button type="button" className="btn-ghost w-full justify-start" onClick={() => downloadCsv(`/reports/export/campaigns?days=${days}`, `campanas_${days}d.csv`)}>
              <Download className="h-4 w-4" /> Campañas
            </button>
            <button type="button" className="btn-ghost w-full justify-start" onClick={() => downloadCsv(`/reports/export/deals?days=${days}`, `deals_${days}d.csv`)}>
              <Download className="h-4 w-4" /> Deals
            </button>
            <button type="button" className="btn-ghost w-full justify-start" onClick={exportCsat}>
              <Download className="h-4 w-4" /> CSAT
            </button>
            <div className="pt-2 border-t border-border/60 space-y-1.5">
              <div className="text-[11px] text-muted-foreground">Rango CSAT (opcional)</div>
              <div className="grid grid-cols-2 gap-1">
                <input type="date" className="input h-8 text-xs" value={csatFrom} onChange={(e) => setCsatFrom(e.target.value)} />
                <input type="date" className="input h-8 text-xs" value={csatTo} onChange={(e) => setCsatTo(e.target.value)} />
              </div>
              <p className="text-[10px] text-muted-foreground">
                Si dejas vacío, usa el periodo del selector ({days}d).
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Tareas abiertas: {k.tasks_open} · Vencidas: {k.tasks_overdue}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="surface p-5">
          <h2 className="font-display font-semibold mb-3">Por agente (incl. tiempo de respuesta)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-2">Agente</th>
                  <th className="py-2 pr-2 text-right">Leads</th>
                  <th className="py-2 pr-2 text-right">Msgs</th>
                  <th className="py-2 pr-2 text-right">1ª resp.</th>
                  <th className="py-2 pr-2 text-right">SLA %</th>
                  <th className="py-2 pr-2 text-right">CSAT</th>
                  <th className="py-2 text-right">Conv. %</th>
                </tr>
              </thead>
              <tbody>
                {data.by_agent.map((a) => (
                  <tr key={a.id} className="border-b border-border/50">
                    <td className="py-2 pr-2">
                      <div className="font-medium">{a.name || a.email}</div>
                      <div className="text-xs text-muted-foreground">{a.email}</div>
                    </td>
                    <td className="py-2 text-right">{a.leads}</td>
                    <td className="py-2 text-right">{a.messages_out}</td>
                    <td className="py-2 text-right tabular-nums">
                      {a.avg_reply_min != null ? (
                        <span title={`Mediana ${a.median_reply_min ?? '—'} · ${a.reply_samples || 0} muestras`}>
                          {a.avg_reply_min}m
                        </span>
                      ) : '—'}
                    </td>
                    <td className={`py-2 text-right tabular-nums ${
                      a.sla_hit_pct != null && a.sla_hit_pct < 70
                        ? 'text-[hsl(var(--danger))]'
                        : a.sla_hit_pct != null && a.sla_hit_pct >= 90
                          ? 'text-emerald-600'
                          : ''
                    }`}
                    >
                      {a.sla_hit_pct != null ? `${a.sla_hit_pct}%` : '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums" title={a.csat_count ? `${a.csat_count} calificaciones` : ''}>
                      {a.csat_avg != null ? a.csat_avg : '—'}
                    </td>
                    <td className="py-2 text-right">{a.conversion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            1ª resp. = minutos hasta responder. SLA % = objetivo en Ajustes. CSAT = promedio del agente (leads asignados).
          </p>
        </div>
        <div className="surface p-5">
          <h2 className="font-display font-semibold mb-3">Campañas recientes</h2>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(data.recent_campaigns || []).map((c) => {
              const st = c.stats || {};
              return (
                <div key={c.id || c.name} className="text-sm border-b border-border/50 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{c.name || 'Campaña'}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {(c.channel || 'whatsapp') === 'email' ? 'Email' : 'WA'} · {c.status}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Enviados {st.sent || 0} · Fallos {st.failed || 0} · Resp. {st.replied || 0}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="surface p-5">
          <h2 className="font-display font-semibold mb-3">Por origen</h2>
          {(data.by_source || []).map((s) => (
            <div key={s.source} className="flex justify-between text-sm border-b border-border/40 py-1.5">
              <span>{s.source}</span>
              <span className="tabular-nums">{s.count}</span>
            </div>
          ))}
        </div>
        <div className="surface p-5">
          <h2 className="font-display font-semibold mb-3">Motivos de pérdida</h2>
          {(data.lost_reasons || []).length === 0 && (
            <p className="text-sm text-muted-foreground">Sin datos (marca leads como Perdido + motivo)</p>
          )}
          {(data.lost_reasons || []).map((s) => (
            <div key={s.reason} className="flex justify-between text-sm border-b border-border/40 py-1.5">
              <span>{s.reason}</span>
              <span className="tabular-nums">{s.count}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function BuilderTab() {
  const [meta, setMeta] = useState(null);
  const [saved, setSaved] = useState([]);
  const [def, setDef] = useState({
    name: '',
    entity: 'leads',
    group_by: 'status',
    metrics: ['count'],
    filters: {},
    days: 30,
    chart_type: 'bar',
    shared: true,
    schedule_enabled: false,
    schedule_freq: 'daily',
    schedule_hour: 8,
    schedule_weekday: 0,
    schedule_emails: [],
  });
  const [scheduleEmailsText, setScheduleEmailsText] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const entityMeta = useMemo(
    () => (meta?.entities || []).find((e) => e.key === def.entity),
    [meta, def.entity],
  );

  const loadMeta = async () => {
    try {
      const [m, s] = await Promise.all([
        api.get('/reports/builder/meta'),
        api.get('/reports/builder'),
      ]);
      setMeta(m.data);
      setSaved(s.data.reports || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar el builder');
    }
  };

  useEffect(() => { loadMeta(); }, []);

  useEffect(() => {
    if (!entityMeta) return;
    const dims = entityMeta.dimensions.map((d) => d.key);
    const mets = entityMeta.metrics.map((m) => m.key);
    setDef((prev) => ({
      ...prev,
      group_by: dims.includes(prev.group_by) ? prev.group_by : dims[0],
      metrics: prev.metrics.filter((m) => mets.includes(m)).length
        ? prev.metrics.filter((m) => mets.includes(m))
        : [mets[0] || 'count'],
      filters: {},
    }));
    // eslint-disable-next-line
  }, [def.entity, entityMeta?.key]);

  const toggleMetric = (key) => {
    setDef((prev) => {
      const set = new Set(prev.metrics || []);
      if (set.has(key)) {
        if (set.size <= 1) return prev;
        set.delete(key);
      } else {
        set.add(key);
      }
      return { ...prev, metrics: [...set] };
    });
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/reports/builder/run', {
        entity: def.entity,
        group_by: def.group_by,
        metrics: def.metrics,
        filters: def.filters,
        days: def.days,
      });
      setResult(data.result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al ejecutar');
    } finally {
      setRunning(false);
    }
  };

  const parseEmails = (text) => text.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()).filter((e) => e.includes('@'));

  const save = async () => {
    if (!def.name?.trim()) return toast.error('Pon un nombre al reporte');
    const payload = {
      ...def,
      schedule_emails: parseEmails(scheduleEmailsText),
    };
    try {
      if (selectedId) {
        await api.patch(`/reports/builder/${selectedId}`, payload);
        toast.success('Reporte actualizado');
      } else {
        const { data } = await api.post('/reports/builder', payload);
        setSelectedId(data.report.id);
        toast.success('Reporte guardado');
      }
      loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  const loadSaved = async (r) => {
    setSelectedId(r.id);
    setDef({
      name: r.name || '',
      entity: r.entity || 'leads',
      group_by: r.group_by || 'status',
      metrics: r.metrics || ['count'],
      filters: r.filters || {},
      days: r.days ?? 30,
      chart_type: r.chart_type || 'bar',
      shared: r.shared !== false,
      description: r.description || '',
      schedule_enabled: !!r.schedule_enabled,
      schedule_freq: r.schedule_freq || 'daily',
      schedule_hour: r.schedule_hour ?? 8,
      schedule_weekday: r.schedule_weekday ?? 0,
      schedule_emails: r.schedule_emails || [],
    });
    setScheduleEmailsText((r.schedule_emails || []).join(', '));
    setRunning(true);
    try {
      const { data } = await api.post(`/reports/builder/${r.id}/run`);
      setResult(data.result);
      if (data.result?.report?.chart_type) {
        setDef((prev) => ({ ...prev, chart_type: data.result.report.chart_type }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setRunning(false);
    }
  };

  const del = async (id) => {
    if (!confirm('¿Eliminar este reporte?')) return;
    try {
      await api.delete(`/reports/builder/${id}`);
      if (selectedId === id) {
        setSelectedId(null);
        setResult(null);
      }
      toast.success('Eliminado');
      loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const newReport = () => {
    setSelectedId(null);
    setResult(null);
    setScheduleEmailsText('');
    setDef({
      name: '',
      entity: 'leads',
      group_by: 'status',
      metrics: ['count'],
      filters: {},
      days: 30,
      chart_type: 'bar',
      shared: true,
      schedule_enabled: false,
      schedule_freq: 'daily',
      schedule_hour: 8,
      schedule_weekday: 0,
      schedule_emails: [],
    });
  };

  const exportCsv = () => {
    if (selectedId) {
      downloadCsv(`/reports/builder/${selectedId}/export?days=${def.days}`, `${def.name || 'reporte'}.csv`);
    } else {
      downloadCsv('/reports/builder/export', 'custom_report.csv', {
        method: 'POST',
        body: {
          entity: def.entity,
          group_by: def.group_by,
          metrics: def.metrics,
          filters: def.filters,
          days: def.days,
        },
      });
    }
  };

  const primaryMetric = def.metrics?.[0] || 'count';
  const chartData = (result?.rows || []).map((r) => ({
    name: r.label?.length > 18 ? `${r.label.slice(0, 16)}…` : r.label,
    full: r.label,
    ...def.metrics.reduce((acc, m) => ({ ...acc, [m]: r[m] ?? 0 }), {}),
  }));

  if (!meta) return <LoadingShell />;

  const fo = meta.filter_options || {};

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      {/* Saved list */}
      <div className="lg:col-span-3 surface p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-sm">Guardados</h3>
          <button type="button" className="btn-ghost h-8 w-8 p-0" onClick={newReport} title="Nuevo">
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-1 max-h-[520px] overflow-y-auto">
          {saved.length === 0 && (
            <p className="text-xs text-muted-foreground py-4 text-center">Aún no hay reportes guardados</p>
          )}
          {saved.map((r) => (
            <div
              key={r.id}
              className={`rounded-[10px] border p-2 text-sm cursor-pointer transition ${
                selectedId === r.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent/40'
              }`}
              onClick={() => loadSaved(r)}
            >
              <div className="flex items-start justify-between gap-1">
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.entity} · {r.group_by} · {r.days ? `${r.days}d` : 'todo'}
                    {r.schedule_enabled ? ' · 📧' : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-ghost h-7 w-7 p-0 text-[hsl(var(--danger))] shrink-0"
                  onClick={(e) => { e.stopPropagation(); del(r.id); }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Definition */}
      <div className="lg:col-span-4 surface p-4 space-y-3 text-sm">
        <h3 className="font-display font-semibold flex items-center gap-2">
          <Wrench className="h-4 w-4" /> Definición
        </h3>
        <label className="block text-xs">
          Nombre
          <input
            className="input mt-1"
            value={def.name}
            onChange={(e) => setDef({ ...def, name: e.target.value })}
            placeholder="Ej. Leads por origen (30d)"
          />
        </label>
        <label className="block text-xs">
          Entidad
          <select
            className="input mt-1"
            value={def.entity}
            onChange={(e) => setDef({ ...def, entity: e.target.value })}
          >
            {(meta.entities || []).map((e) => (
              <option key={e.key} value={e.key}>{e.label}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          Agrupar por
          <select
            className="input mt-1"
            value={def.group_by}
            onChange={(e) => setDef({ ...def, group_by: e.target.value })}
          >
            {(entityMeta?.dimensions || []).map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
        </label>
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1">Métricas</div>
          <div className="flex flex-wrap gap-2">
            {(entityMeta?.metrics || []).map((m) => (
              <label key={m.key} className="chip cursor-pointer inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={(def.metrics || []).includes(m.key)}
                  onChange={() => toggleMetric(m.key)}
                />
                {m.label}
              </label>
            ))}
          </div>
        </div>
        <label className="block text-xs">
          Periodo
          <select
            className="input mt-1"
            value={def.days}
            onChange={(e) => setDef({ ...def, days: Number(e.target.value) })}
          >
            <option value={7}>7 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
            <option value={365}>1 año</option>
            <option value={0}>Todo el tiempo</option>
          </select>
        </label>
        <label className="block text-xs">
          Gráfica
          <select
            className="input mt-1"
            value={def.chart_type}
            onChange={(e) => setDef({ ...def, chart_type: e.target.value })}
          >
            {(meta.chart_types || []).map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </label>

        {/* Filters */}
        {(entityMeta?.filters || []).length > 0 && (
          <div className="border-t border-border pt-3 space-y-2">
            <div className="text-xs font-medium text-muted-foreground">Filtros (opcionales)</div>
            {(entityMeta.filters || []).map((fk) => {
              let options = [];
              if (fk === 'assigned_to') options = fo.users || [];
              else if (fk === 'stage_id') options = fo.stages || [];
              else if (fk === 'pipeline_id') options = fo.pipelines || [];
              else if (fk === 'status' && def.entity === 'leads') options = fo.lead_status || [];
              else if (fk === 'status' && def.entity === 'tasks') options = fo.task_status || [];
              else if (fk === 'status' && def.entity === 'campaigns') {
                options = [
                  { id: 'pending', label: 'pending' },
                  { id: 'running', label: 'running' },
                  { id: 'completed', label: 'completed' },
                  { id: 'paused', label: 'paused' },
                  { id: 'cancelled', label: 'cancelled' },
                ];
              } else if (fk === 'stage') options = fo.deal_stage || [];
              else if (fk === 'channel') options = fo.channel || [];
              else if (fk === 'direction') options = fo.direction || [];

              return (
                <label key={fk} className="block text-xs">
                  {fk}
                  {options.length ? (
                    <select
                      className="input mt-1"
                      value={def.filters?.[fk] || ''}
                      onChange={(e) => setDef({
                        ...def,
                        filters: { ...def.filters, [fk]: e.target.value || undefined },
                      })}
                    >
                      <option value="">— cualquiera —</option>
                      {options.map((o) => (
                        <option key={o.id} value={o.id}>{o.label || o.name || o.id}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="input mt-1"
                      value={def.filters?.[fk] || ''}
                      onChange={(e) => setDef({
                        ...def,
                        filters: { ...def.filters, [fk]: e.target.value || undefined },
                      })}
                      placeholder="valor exacto"
                    />
                  )}
                </label>
              );
            })}
          </div>
        )}

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={def.shared !== false}
            onChange={(e) => setDef({ ...def, shared: e.target.checked })}
          />
          Compartido con el equipo
        </label>

        <div className="border-t border-border pt-3 space-y-2">
          <label className="flex items-center gap-2 text-xs font-medium">
            <input
              type="checkbox"
              checked={!!def.schedule_enabled}
              onChange={(e) => setDef({ ...def, schedule_enabled: e.target.checked })}
            />
            Enviar por email programado
          </label>
          {def.schedule_enabled && (
            <>
              <label className="block text-xs">
                Frecuencia
                <select
                  className="input mt-1"
                  value={def.schedule_freq || 'daily'}
                  onChange={(e) => setDef({ ...def, schedule_freq: e.target.value })}
                >
                  <option value="daily">Diario</option>
                  <option value="weekly">Semanal</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs">
                  Hora (local)
                  <input
                    type="number"
                    min={0}
                    max={23}
                    className="input mt-1"
                    value={def.schedule_hour ?? 8}
                    onChange={(e) => setDef({ ...def, schedule_hour: Number(e.target.value) })}
                  />
                </label>
                {def.schedule_freq === 'weekly' && (
                  <label className="block text-xs">
                    Día
                    <select
                      className="input mt-1"
                      value={def.schedule_weekday ?? 0}
                      onChange={(e) => setDef({ ...def, schedule_weekday: Number(e.target.value) })}
                    >
                      <option value={0}>Lunes</option>
                      <option value={1}>Martes</option>
                      <option value={2}>Miércoles</option>
                      <option value={3}>Jueves</option>
                      <option value={4}>Viernes</option>
                      <option value={5}>Sábado</option>
                      <option value={6}>Domingo</option>
                    </select>
                  </label>
                )}
              </div>
              <label className="block text-xs">
                Destinatarios (emails separados por coma)
                <input
                  className="input mt-1"
                  value={scheduleEmailsText}
                  onChange={(e) => setScheduleEmailsText(e.target.value)}
                  placeholder="jefe@empresa.com, yo@empresa.com"
                />
              </label>
              <p className="text-[10px] text-muted-foreground">
                Requiere SMTP del tenant o global. Se envía HTML con la tabla del reporte.
              </p>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className="btn-primary" disabled={running} onClick={run}>
            <Play className="h-4 w-4" /> {running ? 'Ejecutando…' : 'Ejecutar'}
          </button>
          <button type="button" className="btn-ghost" onClick={save}>
            <Save className="h-4 w-4" /> Guardar
          </button>
          <button type="button" className="btn-ghost" onClick={exportCsv} disabled={!result}>
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>
      </div>

      {/* Result */}
      <div className="lg:col-span-5 surface p-4">
        <h3 className="font-display font-semibold mb-3">Resultado</h3>
        {!result && (
          <p className="text-sm text-muted-foreground py-12 text-center">
            Configura y pulsa Ejecutar para ver la gráfica y la tabla.
          </p>
        )}
        {result && (
          <>
            <div className="text-xs text-muted-foreground mb-3">
              {result.entity_label} por {result.group_by_label}
              {result.days ? ` · últimos ${result.days} días` : ' · todo el tiempo'}
              {' · '}
              {result.total_rows} filas
              {result.total_count != null ? ` · total ${result.total_count}` : ''}
            </div>

            {def.chart_type !== 'table' && chartData.length > 0 && (
              <div className="h-64 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  {def.chart_type === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey={primaryMetric}
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {chartData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 10 }} />
                    </PieChart>
                  ) : (
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 10 }}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ''}
                      />
                      <Legend />
                      {(def.metrics || []).map((m, i) => (
                        <Bar
                          key={m}
                          dataKey={m}
                          name={(result.metrics || []).find((x) => x.key === m)?.label || m}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                          radius={[6, 6, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}

            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-2">{result.group_by_label}</th>
                    {(result.metrics || []).map((m) => (
                      <th key={m.key} className="py-2 text-right">{m.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(result.rows || []).map((row) => (
                    <tr key={String(row.key)} className="border-b border-border/40">
                      <td className="py-1.5 pr-2 font-medium">{row.label}</td>
                      {(result.metrics || []).map((m) => (
                        <td key={m.key} className="py-1.5 text-right tabular-nums">
                          {typeof row[m.key] === 'number'
                            ? Number(row[m.key]).toLocaleString('es-MX')
                            : row[m.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {(result.rows || []).length === 0 && (
                    <tr>
                      <td colSpan={1 + (result.metrics || []).length} className="py-8 text-center text-muted-foreground">
                        Sin datos para estos filtros
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [tab, setTab] = useState('overview');
  const [days, setDays] = useState(30);

  return (
    <div>
      <PageHeader
        title="Reportes"
        description="Dashboard ejecutivo y constructor de reportes personalizados."
        actions={(
          <div className="flex gap-1 rounded-[10px] border border-border p-1">
            <button
              type="button"
              className={`btn-ghost h-8 px-3 text-xs ${tab === 'overview' ? 'bg-accent' : ''}`}
              onClick={() => setTab('overview')}
            >
              Resumen
            </button>
            <button
              type="button"
              data-testid="reports-builder-tab"
              className={`btn-ghost h-8 px-3 text-xs ${tab === 'builder' ? 'bg-accent' : ''}`}
              onClick={() => setTab('builder')}
            >
              <Wrench className="h-3.5 w-3.5" /> Builder
            </button>
          </div>
        )}
      />

      {tab === 'overview' && <OverviewTab days={days} setDays={setDays} />}
      {tab === 'builder' && <BuilderTab />}
    </div>
  );
}
